// Plain-text proposal summary the admin pastes into WhatsApp/e-mail
// ("Copiar proposta"). Single source for both copy buttons on the lead page.
import { itemQty, itemUnit, totalHours, type SummarizableItem } from "./budgetSummary";
import { barterSplit, recurringBilling } from "./barter";
import { formatBRL } from "@/lib/format";

export interface ProposalTextBudget {
  title: string;
  description?: string | null;
  gross_total: number;
  net_total: number;
  recurring_monthly_total?: number;
  platform: string;
  discount_percent?: number;
  /** Permuta: share of net_total settled in services (see src/lib/barter.ts). */
  barter_percent?: number;
  barter_total?: number;
  barter_description?: string | null;
  barter_charge_recurring?: boolean | null;
  items: SummarizableItem[];
}

const PLATFORM_LABELS: Record<string, string> = {
  direct: "Cliente Direto",
  workana: "Workana",
  other: "Outra Plataforma",
};

const fmt = (v: number) => formatBRL(v);

/** Same estimate the budget editor shows: ~6 productive hours per business day. */
export function estimateBusinessDays(hours: number | null | undefined): number | null {
  if (!hours || hours <= 0) return null;
  return Math.ceil(hours / 6);
}

export function buildProposalText(b: ProposalTextBudget, clientName?: string | null): string {
  const items = b.items ?? [];
  const oneTimeItems = items.filter((i) => i.pricing_mode !== "recurring");
  const itemsGross = oneTimeItems.reduce((s, i) => s + i.subtotal, 0);
  const discount = Math.max(itemsGross - b.gross_total, 0);
  const fee = b.gross_total - b.net_total;
  const feePercent = b.gross_total > 0 ? ((fee / b.gross_total) * 100).toFixed(0) : "0";
  const days = estimateBusinessDays(totalHours(items));
  const recurringMonthly =
    b.recurring_monthly_total ??
    items.filter((i) => i.pricing_mode === "recurring").reduce((s, i) => s + i.subtotal, 0);
  const barter = barterSplit(b.net_total, b.barter_percent ?? 0);
  const recurringInBarter = recurringBilling({
    recurring_monthly_total: recurringMonthly,
    barter_percent: b.barter_percent,
    barter_charge_recurring: b.barter_charge_recurring,
  }).inBarter;
  const barterPercentLabel = Number.isInteger(barter.barterPercent)
    ? String(barter.barterPercent)
    : barter.barterPercent.toFixed(1);

  const itemLines =
    items.length > 0
      ? items.map((i) => `• ${i.title} — ${itemQty(i)} × ${itemUnit(i)} = ${fmt(i.subtotal)}`)
      : ["• Preço fixo do projeto"];

  return [
    "📋 PROPOSTA DE ORÇAMENTO",
    "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━",
    "",
    `🔹 Projeto: ${b.title}`,
    b.description ? `🔹 Descrição: ${b.description}` : null,
    clientName ? `🔹 Cliente: ${clientName}` : null,
    "",
    items.length > 1 ? "🧩 SERVIÇOS DO PACOTE" : "💰 VALORES",
    ...itemLines,
    items.length > 1 ? `• Subtotal: ${fmt(itemsGross)}` : null,
    discount > 0 ? `• Desconto do pacote (${b.discount_percent ?? ""}%): - ${fmt(discount)}` : null,
    `• Valor bruto: ${fmt(b.gross_total)}`,
    fee > 0
      ? `• Taxa ${PLATFORM_LABELS[b.platform] ?? b.platform} (${feePercent}%): - ${fmt(fee)}`
      : null,
    `• Valor líquido: ${fmt(b.net_total)}`,
    barter.barterTotal > 0
      ? `• Permuta (${barterPercentLabel}%): - ${fmt(barter.barterTotal)}`
      : null,
    barter.barterTotal > 0
      ? `• Valor a cobrar: ${fmt(barter.cashTotal)}${barter.isFull ? " (permuta integral)" : ""}`
      : null,
    recurringMonthly > 0
      ? `• Mensalidade: ${fmt(recurringMonthly)}/mês${recurringInBarter ? " — incluída na permuta, sem cobrança" : ""}`
      : null,
    ...(barter.barterTotal > 0 && b.barter_description?.trim()
      ? ["", "🤝 CONTRAPARTIDA (PERMUTA)", b.barter_description.trim()]
      : []),
    "",
    "📅 PRAZO ESTIMADO",
    days ? `${days} dias úteis` : "A definir",
    "",
    "---",
    "Eliveuton Souza",
    "Desenvolvimento Web & Automação com IA",
  ]
    .filter((l): l is string => l !== null)
    .join("\n");
}
